import {
  AppBar,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material";

import { useAtom } from "jotai";
import { queryFormAtom } from "../atom";

import { useEffect, useState } from "react";
import { useAdminFetcherWithSwr, useAdminSender } from "../api";
import { MoodCategory, RecommendationResult } from "../generatedApi/spotSearchEngineApi";
import RecommendationMockupListTab from "./RecommendationMockupListTab";
import Template from "./Template";

const mood_category = [
  {
    title: "運動（公園、テーマパーク、スポーツなど）",
    value: MoodCategory.ACTIVE
  },
  {
    title: "見る学ぶ体験（観光地、動物園、神社など）",
    value: MoodCategory.EXPERIENCE
  },
  { title: "買う（ショッピングモール）", value: MoodCategory.SHOPPING },
  { title: "食べる（食全般）", value: MoodCategory.FOOD },
  {
    title: "癒し（銭湯、サウナ、ホテル、自炊型施設）",
    value: MoodCategory.RELAX
  }
];

// 47都道府県名のリスト
const prefectureList = [
  // "北海道", "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県",
  // "茨城県", "栃木県", "群馬県", "埼玉県", "千葉県", "東京都", "神奈川県",
  // "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県",
  "岐阜県",
  "愛知県",
  "三重県",
  "静岡県"
  // "滋賀県", "京都府", "大阪府", "兵庫県", "奈良県", "和歌山県",
  // "鳥取県", "島根県", "岡山県", "広島県", "山口県",
  // "徳島県", "香川県", "愛媛県", "高知県",
  // "福岡県", "佐賀県", "長崎県", "熊本県", "大分県", "宮崎県", "鹿児島県", "沖縄県"
];

const RecommendationMockManagerPage = () => {
  const [queryFormState, setQueryFormState] = useAtom(queryFormAtom);

  const selectedMoodCategoryObjects = mood_category.filter((mc) =>
    queryFormState.queryMoodCategory.includes(mc.value)
  );

  const skipSavedSpotsHandleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQueryFormState((prev) => ({
      ...prev,
      querySkipSavedSpots: (event.target as HTMLInputElement).value === "true"
    }));
  };

  const recomendModeHandleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQueryFormState((prev) => ({
      ...prev,
      recommendMode: (event.target as HTMLInputElement).value as "default" | "experiment"
    }));
  };

  // utilProviderのAPIを利用して、郵便番号から住所を取得する
  const {
    data: location,
    isLoading: locationLoading,
    error: locationError
  } = useAdminFetcherWithSwr({
    apiName: "util_provider",
    serviceName: "default",
    operationId: "getLocation",
    requestParameters: { postalCode: queryFormState.postalCode ?? "" }
  });

  const [recommendation, setRecommendation] = useState<RecommendationResult | null>(null);
  const {
    sender: getRecommend,
    isLoading: recommendLoading,
    error: recommendError
  } = useAdminSender({
    apiName: "spot_search_engine",
    serviceName: "default",
    operationId: "getRecommendation"
  });

  const {
    sender: getExperimentalRecommendation,
    isLoading: experimentalRecommendationLoading,
    error: experimentalRecommendationError
  } = useAdminSender({
    apiName: "spot_search_engine",
    serviceName: "default",
    operationId: "getExperimentalRecommendation"
  });

  const isLoading = locationLoading || recommendLoading || experimentalRecommendationLoading;

  const [localRecommendError, setLocalRecommendError] = useState(null);
  const [localExperimentalRecommendationError, setLocalExperimentalError] = useState(null);

  // 外部フックで取得するエラーを内部ステートに反映
  useEffect(() => {
    if (recommendError) setLocalRecommendError(recommendError);
    if (experimentalRecommendationError) setLocalExperimentalError(experimentalRecommendationError);
  }, [recommendError, experimentalRecommendationError]);

  const error = localRecommendError || localExperimentalRecommendationError;

  const resetError = () => {
    setLocalRecommendError(null);
    setLocalExperimentalError(null);
  };

  const requestRecommendation = async (isInitialRequest: boolean) => {
    if (isInitialRequest) {
      setRecommendation(null);
    }

    let res: RecommendationResult | undefined = undefined;

    const commonRequestBody = {
      user_id: queryFormState.userId,
      location: location,
      address_pref: queryFormState.queryPrefecture,
      mood_category: queryFormState.queryMoodCategory as MoodCategory[],
      genre: queryFormState.queryGenre,
      subgenre: queryFormState.querySubgenre,
      skip_saved_spots: queryFormState.querySkipSavedSpots
    };

    // 推薦モードによって、APIを切り替える
    if (queryFormState.recommendMode === "default") {
      res = await getRecommend({
        requestBody: {
          ...commonRequestBody
        },
        recommendationId: isInitialRequest ? undefined : recommendation?.recommendation_id
      });
    } else if (queryFormState.recommendMode === "experiment") {
      res = await getExperimentalRecommendation({
        requestBody: {
          ...commonRequestBody,
          score_function: queryFormState.queryScoreFunction
        },
        recommendationId: isInitialRequest ? undefined : recommendation?.recommendation_id
      });
    }

    if (res) {
      if (isInitialRequest) {
        setRecommendation(res);
      } else if (recommendation) {
        const totalResult = {
          ...recommendation,
          results: [...recommendation.results, ...res.results]
        };
        setRecommendation(totalResult);
      }
    }
    if (!res) {
      console.error("推薦結果が取得できませんでした");
    }
  };

  const handleSubmit = () => {
    resetError();
    requestRecommendation(true);
  };

  const getMoreRecommend = () => requestRecommendation(false);

  return (
    <Template title="推薦">
      <AppBar component="div" position="static" elevation={0} sx={{ zIndex: 0 }}>
        <Tabs value={"mockup"} textColor="inherit">
          <Tab label="スポット推薦" onClick={() => {}} value="mockup" />
        </Tabs>
      </AppBar>
      <Box component="main" sx={{ flex: 1, px: 2, bgcolor: "#eaeff1" }}>
        <Stack height="100%" direction="row" spacing={2} pt={2}>
          <Stack width={400} spacing={1}>
            <Stack border={(theme) => `1px solid ${theme.palette.divider}`}>
              <Stack
                p={1}
                bgcolor={(theme) => theme.palette.grey[100]}
                spacing={1}
                divider={<Divider />}
              >
                <Stack p={1} spacing={1}>
                  <Typography>推薦ユーザ</Typography>
                </Stack>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                >
                  <Stack p={1} spacing={1}>
                    <TextField
                      label="ユーザーID"
                      placeholder={`user_id を入力...`}
                      value={queryFormState.userId}
                      onChange={(e) =>
                        setQueryFormState((prev) => ({
                          ...prev,
                          userId: e.target.value
                        }))
                      }
                      disabled={recommendLoading}
                    />
                    <Box>
                      <Button
                        type="submit"
                        variant="contained"
                        size="small"
                        disabled={recommendLoading || locationLoading}
                      >
                        推薦を開始
                      </Button>
                    </Box>
                  </Stack>
                </form>
                <Stack p={1} spacing={1}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">推薦モード</FormLabel>
                    <RadioGroup
                      aria-label="recommendMode"
                      name="recommendMode"
                      value={queryFormState.recommendMode}
                      onChange={recomendModeHandleChange}
                    >
                      <FormControlLabel value="default" control={<Radio />} label="デフォルト" />
                      <FormControlLabel value="experiment" control={<Radio />} label="実験" />
                    </RadioGroup>
                  </FormControl>
                </Stack>
                <Stack p={1} spacing={1}>
                  <TextField
                    label="郵便番号"
                    placeholder={`郵便番号 を入力...`}
                    value={queryFormState.postalCode}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      // 数字のみかつ7桁以内の場合のみ更新
                      if (/^\d{0,7}$/.test(newValue)) {
                        setQueryFormState((prev) => ({
                          ...prev,
                          postalCode: newValue
                        }));
                      }
                    }}
                    inputProps={{
                      maxLength: 7, // 最大7文字
                      inputMode: "numeric" // モバイルデバイスで数字キーボードを表示
                    }}
                  />
                  <Autocomplete
                    multiple
                    id="tags-outlined"
                    options={prefectureList}
                    size="small"
                    getOptionLabel={(option) => option}
                    filterSelectedOptions
                    sx={{ pt: 1 }}
                    value={queryFormState.queryPrefecture}
                    onChange={(e, v) => {
                      setQueryFormState((prev) => ({
                        ...prev,
                        queryPrefecture: v
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="都道府県" placeholder="都道府県を選択..." />
                    )}
                  />
                  <Autocomplete
                    multiple
                    id="tags-outlined"
                    options={mood_category}
                    size="small"
                    getOptionLabel={(option) => option.title}
                    filterSelectedOptions
                    sx={{ pt: 1 }}
                    value={selectedMoodCategoryObjects}
                    onChange={(e, v) => {
                      setQueryFormState((prev) => ({
                        ...prev,
                        queryMoodCategory: v.map((mood) => mood.value)
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="目的ジャンル" placeholder="ジャンルを選択..." />
                    )}
                  />
                  <Autocomplete
                    multiple
                    freeSolo
                    id="genre"
                    value={queryFormState.queryGenre || undefined}
                    options={[]}
                    onChange={(e, v) => {
                      setQueryFormState((prev) => ({
                        ...prev,
                        queryGenre: v
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="ジャンル" placeholder="ジャンルを入力..." />
                    )}
                  />
                  <Autocomplete
                    multiple
                    freeSolo
                    id="subgenre"
                    value={queryFormState.querySubgenre || undefined}
                    options={[]}
                    onChange={(e, v) => {
                      setQueryFormState((prev) => ({
                        ...prev,
                        querySubgenre: v
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="サブジャンル"
                        placeholder="サブジャンルを入力..."
                      />
                    )}
                  />
                  <TextField
                    multiline
                    label="score_function"
                    placeholder={`score_function を入力...`}
                    value={queryFormState.queryScoreFunction}
                    onChange={(e) =>
                      setQueryFormState((prev) => ({
                        ...prev,
                        queryScoreFunction: e.target.value
                      }))
                    }
                    disabled={queryFormState.recommendMode === "default"}
                    sx={{
                      "& .MuiInputBase-root": {
                        fontFamily: "monospace"
                      }
                    }}
                  />
                  <FormControl component="fieldset">
                    <FormLabel component="legend">skip_saved_spots</FormLabel>
                    <RadioGroup
                      aria-label="skipSavedSpots"
                      name="skipSavedSpots"
                      value={queryFormState.querySkipSavedSpots}
                      onChange={skipSavedSpotsHandleChange}
                    >
                      <FormControlLabel value="true" control={<Radio />} label="true" />
                      <FormControlLabel value="false" control={<Radio />} label="false" />
                    </RadioGroup>
                  </FormControl>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          <Stack width="100%">
            <Stack
              border={(theme) => `1px solid ${theme.palette.divider}`}
              bgcolor={(theme) => theme.palette.background.default}
            >
              {isLoading && !error && (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <CircularProgress />
                </Box>
              )}

              {locationError && queryFormState.postalCode !== "" && (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="error">
                    郵便番号が見つからないため、推薦情報に距離を含めていません。調査対象府県の郵便番号かどうか確かめてください。
                  </Typography>
                </Box>
              )}
              {error && (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="error">
                    エラーが発生しました:{" "}
                    {recommendError && JSON.stringify(recommendError, null, 2)}{" "}
                    {experimentalRecommendationError &&
                      JSON.stringify(experimentalRecommendationError, null, 2)}{" "}
                  </Typography>
                </Box>
              )}

              {recommendation ? (
                <Box>
                  <RecommendationMockupListTab
                    RecommendationItems={recommendation.results}
                    getMoreRecommend={getMoreRecommend}
                    isLoading={isLoading}
                  />
                </Box>
              ) : (
                "推薦を開始ボタンを押してください"
              )}
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Template>
  );
};

export default RecommendationMockManagerPage;
